<template lang="pug">
.fb-settings(v-if="element" :class="{ 'is-embedded': embedded }")
    .fb-settings-head(v-if="!embedded")
        h4.fb-settings-title
            i(:class="icon")
            span {{ typeName }}
        el-button(
            type="text"
            icon="el-icon-fa-xmark fas"
            @click="$emit('close')"
        )
    .fb-settings-body
        el-form(label-position="top" size="mini" @submit.native.prevent)
            el-form-item
                template(#label)
                    span {{ labelFieldName }}
                    el-tooltip(:content="$t('label-help')" placement="top")
                        i.fb-help.el-icon-fa-circle-question.fas
                el-input(v-model="element.label" @input="emitChange")
            el-form-item(v-if="hasPlaceholder")
                template(#label)
                    span {{ $t('Placeholder') }}
                    el-tooltip(:content="$t('placeholder-help')" placement="top")
                        i.fb-help.el-icon-fa-circle-question.fas
                el-input(v-model="element.placeholder" @input="emitChange")
            el-form-item(v-if="element.type === 'textarea'")
                template(#label)
                    span {{ $t('Rows') }}
                    el-tooltip(:content="$t('rows-help')" placement="top")
                        i.fb-help.el-icon-fa-circle-question.fas
                el-input-number(v-model="element.rows" :min="2" :max="20" controls-position="right" @change="emitChange")
            el-form-item(v-if="element.type === 'consent'")
                template(#label)
                    span {{ $t('Privacy page') }}
                    el-tooltip(:content="$t('privacy-page-help')" placement="top")
                        i.fb-help.el-icon-fa-circle-question.fas
                treeselect(
                    v-model="privacyPageId"
                    :placeholder="$t('Select page …')"
                    :multiple="false"
                    :options="pageOptions"
                    :default-expand-level="Infinity"
                    :close-on-select="true"
                    :searchable="true"
                    :normalizer="node => ({ label: node.title, ...node })"
                    :clearable="true"
                )
            template(v-if="element.type === 'slider'")
                el-form-item(:label="$t('Minimum')")
                    el-input-number(v-model="element.min" controls-position="right" @change="emitChange")
                el-form-item(:label="$t('Maximum')")
                    el-input-number(v-model="element.max" controls-position="right" @change="emitChange")
                el-form-item
                    template(#label)
                        span {{ $t('Step size') }}
                        el-tooltip(:content="$t('step-help')" placement="top")
                            i.fb-help.el-icon-fa-circle-question.fas
                    el-input-number(v-model="element.step" :min="1" controls-position="right" @change="emitChange")
                el-form-item
                    template(#label)
                        span {{ $t('Unit') }}
                        el-tooltip(:content="$t('unit-help')" placement="top")
                            i.fb-help.el-icon-fa-circle-question.fas
                    el-input(v-model="element.unit" :placeholder="$t('e.g. €, %, km')" @input="emitChange")
            template(v-if="element.type === 'upload'")
                el-form-item(:label="$t('Max files')")
                    el-input-number(v-model="element.maxFiles" :min="1" :max="20" controls-position="right" @change="emitChange")
                el-form-item(:label="$t('Allowed file types')")
                    el-select.is-full-width(
                        v-model="element.accept"
                        multiple
                        :placeholder="$t('Any file type')"
                        @change="emitChange"
                    )
                        el-option(v-for="opt in acceptOptions" :key="opt.key" :label="opt.label" :value="opt.key")
                .fb-settings-hint {{ $t('upload-limit-hint') }}
            el-form-item(v-if="element.type === 'select'")
                el-switch(v-model="element.multiple" :active-text="$t('Multiple selection')" @change="emitChange")
            template(v-if="element.type === 'date'")
                el-form-item
                    template(#label)
                        span {{ $t('Date mode') }}
                        el-tooltip(:content="$t('date-mode-help')" placement="top")
                            i.fb-help.el-icon-fa-circle-question.fas
                    el-select.is-full-width(v-model="element.mode" @change="emitChange")
                        el-option(:label="$t('date-mode-date')" value="date")
                        el-option(:label="$t('date-mode-datetime')" value="datetime")
                        el-option(:label="$t('date-mode-time')" value="time")
                el-form-item(v-if="element.mode !== 'time'")
                    template(#label)
                        span {{ $t('Earliest date') }}
                        el-tooltip(:content="$t('earliest-date-help')" placement="top")
                            i.fb-help.el-icon-fa-circle-question.fas
                    el-date-picker.is-full-width(v-model="element.min" type="date" value-format="yyyy-MM-dd" clearable @change="emitChange")
                el-form-item(v-if="element.mode !== 'time'")
                    template(#label)
                        span {{ $t('Latest date') }}
                        el-tooltip(:content="$t('latest-date-help')" placement="top")
                            i.fb-help.el-icon-fa-circle-question.fas
                    el-date-picker.is-full-width(v-model="element.max" type="date" value-format="yyyy-MM-dd" clearable @change="emitChange")
            el-form-item(v-if="element.type === 'country'")
                template(#label)
                    span {{ $t('Default country') }}
                    el-tooltip(:content="$t('default-country-help')" placement="top")
                        i.fb-help.el-icon-fa-circle-question.fas
                el-select.is-full-width(
                    v-model="element.defaultCountry"
                    filterable
                    clearable
                    :placeholder="$t('No preset')"
                    @change="emitChange"
                )
                    el-option(v-for="(label, value) in countryOptions" :key="value" :label="label" :value="value")
            el-form-item(v-if="canRequire")
                el-switch(v-model="element.required" :active-text="$t('Required field')" @change="emitChange")
            el-form-item(v-if="hasRole" :label="$t('Field role')")
                el-select.is-full-width(v-model="element.role" @change="emitChange")
                    el-option(:label="$t('role-custom')" value="custom")
                    el-option(:label="$t('role-tel')" value="tel")
                    el-option(:label="$t('role-url')" value="url")
                    el-option(:label="$t('role-number')" value="number")
                    el-option(:label="$t('role-hidden')" value="hidden")
                .fb-settings-hint {{ $t('role-hint') }}
            el-form-item(v-if="element.role === 'hidden'")
                template(#label)
                    span {{ $t('Value') }}
                    el-tooltip(:content="$t('hidden-value-help')" placement="top")
                        i.fb-help.el-icon-fa-circle-question.fas
                el-input(v-model="element.defaultValue" :placeholder="$t('Fixed value')" @input="emitChange")
            el-form-item(v-if="element.type === 'submit'" :label="$t('Button alignment')")
                el-radio-group(v-model="element.align" size="mini" @change="emitChange")
                    el-radio-button(label="left") {{ $t('Left') }}
                    el-radio-button(label="right") {{ $t('Right') }}
                    el-radio-button(label="full") {{ $t('Full width') }}
            el-form-item(v-if="hasInlineToggle" :label="$t('Alignment')")
                el-radio-group(v-model="element.inline" size="mini" @change="emitChange")
                    el-radio-button(:label="false") {{ $t('Below each other') }}
                    el-radio-button(:label="true") {{ $t('Next to each other') }}
            template(v-if="hasOptions")
                el-form-item(:label="$t('Options')")
                    .fb-option(v-for="(option, i) in element.options" :key="i")
                        el-input(
                            v-model="option.value"
                            size="mini"
                            :placeholder="$t('Option label')"
                            @input="onOptionInput(option)"
                        )
                        el-button(
                            circle
                            size="mini"
                            type="danger"
                            icon="el-icon-fa-trash-can fas"
                            :disabled="element.options.length <= 1"
                            @click="removeOption(i)"
                        )
                    el-button(
                        size="mini"
                        icon="el-icon-fa-plus fas"
                        @click="addOption"
                    ) {{ $t('Add option') }}
</template>

<i18n>
{
    "en": {
        "Placeholder": "Placeholder",
        "label-help": "The visible caption shown above or next to the field in the form.",
        "placeholder-help": "Example text shown inside the empty field; it disappears once the user starts typing.",
        "Rows": "Rows",
        "rows-help": "Height of the text area – the number of visible lines before scrolling.",
        "Minimum": "Minimum",
        "Maximum": "Maximum",
        "Step size": "Step size",
        "step-help": "The increment between two selectable values.",
        "Unit": "Unit",
        "unit-help": "Optional unit shown next to the value (e.g. €, %, km).",
        "e.g. €, %, km": "e.g. €, %, km",
        "Max files": "Max. number of files",
        "Allowed file types": "Allowed file types",
        "Any file type": "Any file type",
        "upload-limit-hint": "Files are sent as e-mail attachments — e-mail clients usually cap the total at about 10 MB. Keep the number of files and their size low.",
        "Required field": "Required field",
        "Multiple selection": "Multiple selection",
        "Privacy page": "Privacy page",
        "privacy-page-help": "Page the privacy link points to. A matching page is preselected automatically.",
        "Select page …": "Select page …",
        "Date mode": "Date mode",
        "date-mode-help": "Whether the field asks for a date, a date with time, or only a time.",
        "date-mode-date": "Date",
        "date-mode-datetime": "Date & time",
        "date-mode-time": "Time",
        "Earliest date": "Earliest date",
        "earliest-date-help": "The earliest date that can be selected. Leave empty for no limit.",
        "Latest date": "Latest date",
        "latest-date-help": "The latest date that can be selected. Leave empty for no limit.",
        "Default country": "Default country",
        "default-country-help": "Country preselected when the form loads. Leave empty for no preselection.",
        "No preset": "No preset",
        "Alignment": "Alignment",
        "Below each other": "Below each other",
        "Next to each other": "Next to each other",
        "Button alignment": "Button alignment",
        "Left": "Left",
        "Right": "Right",
        "Full width": "Full width",
        "Field role": "Field role",
        "Options": "Options",
        "Option label": "Option label",
        "Add option": "Add option",
        "Label": "Label",
        "Text": "Text",
        "Button label": "Button label",
        "role-custom": "Standard field",
        "role-tel": "Phone number",
        "role-url": "Website (URL)",
        "role-number": "Number",
        "role-hidden": "Hidden field",
        "Value": "Value",
        "Fixed value": "Fixed value",
        "hidden-value-help": "Fixed value that is sent invisibly with the form (e.g. a source or campaign tag).",
        "role-hint": "The role defines the field type and how the entry is validated."
    },
    "de": {
        "Placeholder": "Platzhalter",
        "label-help": "Die sichtbare Beschriftung, die im Formular über bzw. neben dem Feld angezeigt wird.",
        "placeholder-help": "Beispieltext im leeren Feld; er verschwindet, sobald der Nutzer etwas eingibt.",
        "Rows": "Zeilen",
        "rows-help": "Höhe des Textfeldes – Anzahl der sichtbaren Zeilen, bevor gescrollt wird.",
        "Minimum": "Minimum",
        "Maximum": "Maximum",
        "Step size": "Schrittweite",
        "step-help": "Der Abstand zwischen zwei wählbaren Werten.",
        "Unit": "Einheit",
        "unit-help": "Optionale Einheit, die neben dem Wert angezeigt wird (z. B. €, %, km).",
        "e.g. €, %, km": "z. B. €, %, km",
        "Max files": "Max. Anzahl Dateien",
        "Allowed file types": "Erlaubte Dateitypen",
        "Any file type": "Alle Dateitypen",
        "upload-limit-hint": "Dateien werden als E-Mail-Anhang versendet — E-Mail-Programme begrenzen die Gesamtgröße meist auf ca. 10 MB. Anzahl und Größe der Dateien entsprechend gering halten.",
        "Required field": "Pflichtfeld",
        "Multiple selection": "Mehrfachauswahl",
        "Privacy page": "Datenschutzseite",
        "privacy-page-help": "Seite, auf die der Datenschutz-Link verweist. Eine passende Seite wird automatisch vorausgewählt.",
        "Select page …": "Seite auswählen …",
        "Date mode": "Datums-Modus",
        "date-mode-help": "Ob das Feld nach einem Datum, Datum mit Uhrzeit oder nur einer Uhrzeit fragt.",
        "date-mode-date": "Datum",
        "date-mode-datetime": "Datum & Uhrzeit",
        "date-mode-time": "Uhrzeit",
        "Earliest date": "Frühestes Datum",
        "earliest-date-help": "Das früheste wählbare Datum. Leer lassen für keine Begrenzung.",
        "Latest date": "Spätestes Datum",
        "latest-date-help": "Das späteste wählbare Datum. Leer lassen für keine Begrenzung.",
        "Default country": "Standardland",
        "default-country-help": "Land, das beim Laden des Formulars vorausgewählt ist. Leer lassen für keine Vorauswahl.",
        "No preset": "Keine Vorauswahl",
        "Alignment": "Ausrichtung",
        "Below each other": "Untereinander",
        "Next to each other": "Nebeneinander",
        "Button alignment": "Button-Ausrichtung",
        "Left": "Links",
        "Right": "Rechts",
        "Full width": "Volle Breite",
        "Field role": "Feld-Rolle",
        "Options": "Optionen",
        "Option label": "Bezeichnung",
        "Add option": "Option hinzufügen",
        "Label": "Beschriftung",
        "Text": "Text",
        "Button label": "Button-Text",
        "role-custom": "Standardfeld",
        "role-tel": "Telefonnummer",
        "role-url": "Webseite (URL)",
        "role-number": "Zahl",
        "role-hidden": "Verstecktes Feld",
        "Value": "Wert",
        "Fixed value": "Fester Wert",
        "hidden-value-help": "Fester Wert, der unsichtbar mit dem Formular gesendet wird (z. B. eine Quelle oder Kampagnen-Kennung).",
        "role-hint": "Die Rolle legt den Feldtyp fest und wie die Eingabe geprüft wird."
    }
}
</i18n>

<script>
import _ from 'lodash'
import uniqid from 'uniqid'
import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import de from 'i18n-iso-countries/langs/de.json'
import Treeselect from '@riophae/vue-treeselect'
import NestedSet from '@/nested-set-lib'
import { useSetupStore } from '@/stores'
import { elementIcon, elementTypeName } from './palette'

countries.registerLocale(en)
countries.registerLocale(de)

const PLACEHOLDER_TYPES = ['name', 'text', 'email', 'textarea', 'select', 'country']
const OPTION_TYPES = ['select', 'radio', 'checkbox']
const PRESENTATIONAL_TYPES = ['heading', 'paragraph', 'submit']

export default {
    name: 'FormBuilderElementSettings',
    components: { Treeselect },
    props: {
        element: { type: Object, default: null },
        embedded: { type: Boolean, default: false },
        options: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            acceptOptions: [
                { key: 'image', label: 'Bilder (JPG, PNG, GIF, WEBP)' },
                { key: 'pdf', label: 'PDF' },
                { key: 'word', label: 'Word (DOC, DOCX)' },
                { key: 'excel', label: 'Excel (XLS, XLSX)' },
                { key: 'text', label: 'Text (TXT, CSV)' },
                { key: 'archive', label: 'Archive (ZIP, RAR)' },
            ],
        }
    },
    watch: {
        // normalise legacy upload elements that predate these fields
        element: {
            immediate: true,
            handler(el) {
                if (el && el.type === 'upload') {
                    if (el.maxFiles == null) this.$set(el, 'maxFiles', 1)
                    if (!Array.isArray(el.accept)) this.$set(el, 'accept', [])
                }
                if (el && el.type === 'submit' && !el.align) {
                    this.$set(el, 'align', 'right')
                }
                this.autoDetectPrivacyPage(el)
            },
        },
    },
    computed: {
        icon() {
            return this.element ? elementIcon(this.element.type) : ''
        },
        typeName() {
            return this.element ? elementTypeName(this.element.type) : ''
        },
        labelFieldName() {
            if (!this.element) return this.$t('Label')
            if (this.element.type === 'submit') return this.$t('Button label')
            if (this.element.type === 'paragraph') return this.$t('Text')
            return this.$t('Label')
        },
        hasPlaceholder() {
            return this.element && PLACEHOLDER_TYPES.includes(this.element.type)
        },
        hasOptions() {
            return this.element && OPTION_TYPES.includes(this.element.type)
        },
        hasInlineToggle() {
            return this.element && ['radio', 'checkbox'].includes(this.element.type)
        },
        countryOptions() {
            const locale = this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'de'
            return countries.getNames(locale)
        },
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        // flat list of all selectable menu items (for id lookup + auto-detect)
        allMenuItems() {
            const menuOptions = _.get(this.options, 'menu.items') || []
            return menuOptions.reduce((acc, mo) => acc.concat(mo.menuItems || []), [])
        },
        // page tree for the treeselect (mirrors BannerLinkPicker.pageOptions)
        pageOptions() {
            let menus = _.get(this.activeCms, 'config.template.menus', [])
            menus = _.cloneDeep(menus).sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))
            const menuOptions = _.get(this.options, 'menu.items') || []
            const visibleOptgroups = menuOptions.filter(el => el.menuItems.length).length > 1
            let tree = []
            menus.forEach(menu => {
                const menuOption = _.find(menuOptions, { key: menu.key })
                const items = _.get(menuOption, 'menuItems', [])
                if (items.length) {
                    const optgroup = {
                        id: `optgroup-${menuOption.id}`,
                        title: menuOption.name,
                        isDisabled: true,
                        optgroup: true,
                    }
                    if (visibleOptgroups) tree.push(optgroup)
                    tree = [...tree, ...NestedSet.createTree(items)]
                }
            })
            return tree
        },
        privacyPageId: {
            get() {
                return _.get(this.element, 'link.id') || null
            },
            set(id) {
                const found = this.allMenuItems.find(item => item.id === id)
                this.$set(this.element, 'link', found ? { id: found.id, title: found.title, path: found.path } : null)
                this.emitChange()
            },
        },
        canRequire() {
            return this.element && !PRESENTATIONAL_TYPES.includes(this.element.type)
        },
        hasRole() {
            // name/email keep their fixed role; only generic input fields expose the selector
            return this.element && ['text'].includes(this.element.type)
        },
    },
    methods: {
        emitChange() {
            this.$emit('change')
        },
        // On first display of a consent field (no link chosen yet) preselect a
        // menu item whose title looks like a privacy page ("Datenschutz…").
        // Runs only while link is untouched (undefined); a deliberately cleared
        // link is null and stays that way.
        autoDetectPrivacyPage(el) {
            if (!el || el.type !== 'consent' || el.link !== undefined) return
            const match = this.allMenuItems.find(item => /datenschutz/i.test(item.title || ''))
            this.$set(el, 'link', match ? { id: match.id, title: match.title, path: match.path } : null)
            if (match) this.emitChange()
        },
        onOptionInput(option) {
            if (!option.key) this.$set(option, 'key', uniqid('opt-'))
            this.emitChange()
        },
        addOption() {
            if (!this.element.options) this.$set(this.element, 'options', [])
            this.element.options.push({ key: uniqid('opt-'), value: '' })
            this.emitChange()
        },
        removeOption(index) {
            this.element.options.splice(index, 1)
            this.emitChange()
        },
    },
}
</script>

<style lang="sass" scoped>
.fb-settings
    flex: 0 0 260px
    align-self: flex-start
    background: #fff
    border: 1px solid #e4e7ed
    border-radius: 8px
    &.is-embedded
        flex: 1 1 auto
        border: none
        border-radius: 0
.fb-settings-head
    display: flex
    align-items: center
    justify-content: space-between
    padding: .6rem .75rem
    border-bottom: 1px solid #ebeef5
.fb-settings-title
    margin: 0
    display: flex
    align-items: center
    gap: .5rem
    font-size: .9rem
    i
        color: $--color-primary
.fb-settings-body
    padding: .75rem
    // The parent .is-form-builder rule in InputField.vue hides every
    // el-form-item label and zeroes spacing; restore both inside the settings.
    ::v-deep .el-form-item
        margin-bottom: 0.75rem !important
        &:last-child
            margin-bottom: 0 !important
    ::v-deep .el-form-item__label
        display: block !important
        float: none
        padding: 0 0 2px
        line-height: 1.3
        font-size: .75rem
        color: #606266
    ::v-deep .el-form-item__content
        margin-left: 0 !important
        line-height: 1.2
.fb-help
    margin-left: .3rem
    color: #c0c4cc
    font-size: .8rem
    cursor: help
    &:hover
        color: $--color-primary
.fb-settings-hint
    margin-top: .35rem
    font-size: .72rem
    color: #909399
    line-height: 1.3
.fb-option
    display: flex
    align-items: center
    gap: .4rem
    margin-bottom: .4rem
.is-full-width
    width: 100%
</style>

<style lang="sass">
@import '~@riophae/vue-treeselect/dist/vue-treeselect.css'

.fb-settings-body .vue-treeselect
    font-size: 12px
    width: 100%
    .vue-treeselect__control
        height: 28px
        border-color: #dcdfe6
    .vue-treeselect__placeholder,
    .vue-treeselect__single-value
        line-height: 26px
        font-size: 12px
    .vue-treeselect__menu-container
        width: 100% !important
</style>
